import * as React from "react";
import { Box, Card, Chip, Stack, Typography } from "@mui/material";

import type { CampaignRequestAgent, CampaignRequestMarket } from "@/hooks/api/use-campaign-requests";

import {
	agentKindLabel,
	formatCallingDays,
	formatDateRange,
	formatWallClock,
	languageLabel,
	marketClock,
	marketFlag,
	marketLabel,
} from "../constants";

export interface MarketScheduleCardProps {
	market: CampaignRequestMarket;
	/** The agent row that answers here, where the caller has it. Several markets share one. */
	agent?: CampaignRequestAgent | null;
	/** Edit / remove controls, or anything else the screen wants in the header. */
	actions?: React.ReactNode;
	children?: React.ReactNode;
}

function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
	return (
		<Box sx={{ minWidth: 150 }}>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="body2">{value}</Typography>
		</Box>
	);
}

/**
 * What to call the agent on the chip — one name, not the same name twice.
 *
 * The API defaults an agent's nickname to the language it answers in, so `label` and
 * `languageLabel(language)` are the identical string on almost every real row and this chip read
 * "Bahasa Indonesia · Bahasa Indonesia". They are two facts only when somebody has actually
 * renamed the agent, so that is the only case that gets two values.
 */
function agentChipLabel(agent: CampaignRequestAgent): string {
	const language = languageLabel(agent.language);
	const nickname = agent.label.trim();

	if (nickname.length === 0 || nickname === language) return language;
	return `${nickname} · ${language}`;
}

/**
 * One market and everything that decides when it dials. Each of these cards is literally a
 * campaign — that is the whole architecture, surfaced as cards instead of hidden objects.
 *
 * Every time on the row is local to the market's own zone, and the zone is printed beside them.
 * 09:00 SGT is not 09:00 AEST, and a shared window that silently dials Australia at the wrong
 * local hour is the single most common way a multi-market schedule goes wrong.
 */
export function MarketScheduleCard({ market, agent, actions, children }: MarketScheduleCardProps): React.JSX.Element {
	const clock = marketClock(market.market);
	const window =
		market.callingWindowStart || market.callingWindowEnd
			? `${formatWallClock(market.callingWindowStart)} – ${formatWallClock(market.callingWindowEnd)}`
			: "—";

	return (
		<Card variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
					<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
						<Typography variant="h6">
							{marketFlag(market.market)} {marketLabel(market.market)}
						</Typography>

						{agent ? (
							<Chip label={agentChipLabel(agent)} size="small" variant="soft" color="primary" />
						) : (
							// A pill is a tag, not a sentence — and the wizard's own card says "No agent"
							// for the identical state two screens earlier.
							<Chip label="No agent" size="small" variant="outlined" color="warning" />
						)}

						{/* Position zero is the market the client led with, and the programme reports in its zone. */}
						{market.position === 0 ? <Chip label="Lead market" size="small" variant="outlined" /> : null}

						{market.campaignId ? <Chip label="Provisioned" size="small" variant="soft" color="success" /> : null}
					</Stack>

					{actions}
				</Stack>

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
					<Field label="Dates" value={formatDateRange(market.dialStartDate, market.dialEndDate)} />
					<Field
						label="Calling window"
						value={
							<React.Fragment>
								{window}
								{clock ? (
									<Typography component="span" variant="body2" color="primary" sx={{ ml: 0.5 }}>
										{clock}
									</Typography>
								) : null}
							</React.Fragment>
						}
					/>
					<Field label="Days" value={formatCallingDays(market.callingDays)} />
					{/* The same words step 3 asks the question in, so one setting is not two names. */}
					<Field
						label="Try again after"
						value={market.redialIntervalHours ? `${market.redialIntervalHours} hours` : "—"}
					/>
					{/* A contract term rather than a setting: it is enforced per market. */}
					<Field label="Maximum attempts per person" value={market.maxAttemptsPerPerson ?? "—"} />

					{/*
					 * The zone, once. "SGT" already sits against the calling window in the accent colour,
					 * which is the form the client reads a time in, so printing "Asia/Singapore" beside it
					 * is the same fact twice. The IANA name is shown only where we have no short form for
					 * it — a schedule must never leave its zone unstated.
					 */}
					{clock ? null : <Field label="Timezone" value={market.timezone} />}
					{agent ? <Field label="Agent" value={agentKindLabel(agent.kind)} /> : null}
				</Box>

				{children}
			</Stack>
		</Card>
	);
}
